import * as angular from 'angular';

import { IRequestService } from '../request.service';
import { ActionResponseDto, IFileInformation, Directory } from '../../models';
import { TicketAttachmentSettings } from '../../models/Ticketing/TicketAttachmentSettings';
import { IUploadService } from '../helpers';

export interface ITicketAttachmentService {
    getTicketAttachments(ticketId: number): angular.IPromise<ActionResponseDto<IFileInformation>>;
    getTicketAttachmentsAsTree(ticketId: number): angular.IPromise<Directory>;
    deleteAttachment(ticketId: number, fileName: string): angular.IPromise<ActionResponseDto<IFileInformation>>;
    downloadAttachment(ticketId: number, fileName: string): angular.IPromise<Blob>;
    uploadAttachment(ticketId: number, file: File): angular.IPromise<ActionResponseDto<IFileInformation>>;
    uploadAttachments(ticketId: number, files: File[]): angular.IPromise<ActionResponseDto<IFileInformation>[]>;
    settings(): angular.IPromise<TicketAttachmentSettings>;
}

export class TicketAttachmentService implements ITicketAttachmentService {
    static serviceId = 'ticketAttachmentService';
    private _controllerUrl = 'ticket/attachment';

    static $inject = [
        '$q',
        'requestService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _uploadService: IUploadService
    ) {
    }

    getTicketAttachments(ticketId: number): angular.IPromise<ActionResponseDto<IFileInformation>> {
        if (ticketId) {
            const url = `${this._controllerUrl}/${ticketId}/`;
            this._requestService.cleanCacheAtV1(url);
            return this._requestService
                .getAtV1<ActionResponseDto<IFileInformation>>(url)
                .then(data => {
                    if (data.isSuccessful) {
                        return data.items;
                    } else {
                        throw data.message;
                    }
                })
                .catch(error => {
                    return error;
                });
        } else {
            return this._$q.reject();
        }
    }

    getTicketAttachmentsAsTree(ticketId: number): angular.IPromise<Directory> {
        if (ticketId) {
            const url = `${this._controllerUrl}/tree/${ticketId}/`;
            this._requestService.cleanCacheAtV1(url);
            return this._requestService.getAtV1(url);
        } else {
            return this._$q.reject();
        }
    }

    deleteAttachment(ticketId: number, fileName: string): angular.IPromise<ActionResponseDto<IFileInformation>> {
        if (ticketId && fileName) {
            const url = `${this._controllerUrl}/delete/${ticketId}/`;
            const cleanUrl = `${this._controllerUrl}/tree/${ticketId}/`;
            this._requestService.cleanCacheAtV1(cleanUrl);
            const data = JSON.stringify(fileName);
            return this._requestService
                .postAtV1<ActionResponseDto<IFileInformation>>(url, data)
                .then(data => {
                    if (data.isSuccessful) {
                        return data.items;
                    } else {
                        throw data.message;
                    }
                })
                .catch(error => {
                    return error;
                });
        } else {
            return this._$q.reject();
        }
    }

    downloadAttachment(ticketId: number, fileName: string): angular.IPromise<Blob> {
        if (ticketId && fileName) {
            const url = `${this._controllerUrl}/download/${ticketId}/`;
            const data = JSON.stringify(fileName);
            return this._requestService
                .postAtV1ForFile<any>(url, data)
                .then(data => {
                    const contentType = 'application/octet-stream';
                    const blob = new Blob([data], { type: contentType });
                    return blob;
                });
        } else {
            return this._$q.reject();
        }
    }

    uploadAttachment(ticketId: number, file: File): angular.IPromise<ActionResponseDto<IFileInformation>> {
        if (ticketId && file) {
            const url = `${this._requestService.getUrlV1()}/${this._controllerUrl}/upload/${ticketId}/`;
            return this._uploadService.uploadFile<ActionResponseDto<IFileInformation>>(url, file);
        } else {
            return this._$q.reject();
        }
    }

    uploadAttachments(ticketId: number, file: File[]): angular.IPromise<ActionResponseDto<IFileInformation>[]> {
        return this._$q.all(file.map(f => this.uploadAttachment(ticketId, f)));
    }

    settings(): angular.IPromise<TicketAttachmentSettings> {
        return this._requestService.getAtV1(`${this._controllerUrl}/settings`);
    }
}